import dimensionsJson from '../dimensions.json';
import moment from 'moment';
import { UsersCategoryType } from '../models';
import { getChartTranslation } from '../Utils';
import { MetricsFeatures } from 'modules/metrics/MetricsFeatures';
const dimensions = dimensionsJson;
const GET_ACTIVE_USERS_BY_DAY_TIMEOUT = 90000;
export class UserAnalyticsService {
    static $inject = ['StatisticsService2', 'MetricsService', '$translate', 'MessagingHubService'];
    constructor(StatisticsService2, MetricsService, $translate, MessagingHubService) {
        'ngInject';
        this.StatisticsService2 = StatisticsService2;
        this.MetricsService = MetricsService;
        this.$translate = $translate;
        this.MessagingHubService = MessagingHubService;
    }
    getCategories(chartType) {
        const dimension = dimensions.usersMau;
        const categoryKeys = Object.keys(dimension);
        const categories = [];
        categoryKeys.forEach((category) => {
            const translation = getChartTranslation(this.$translate, category, chartType, 'users');
            const value = this.$translate.instant(translation);
            categories.push({ key: category, value: value });
        });
        return categories;
    }
    async getActionsForCategory({ chartType, category, startDate, endDate, application, }) {
        //TODO: refactor to single chart component calls
        let result;
        switch (category) {
            case UsersCategoryType.UniqueUsers:
                result =
                    chartType == 'line'
                        ? await this.getActionsUniqueUsersByDay({
                            shortName: application.shortName,
                            startDate,
                            endDate,
                        }) //Get active users by day even they are the same
                        : await this.getActionsUniqueUsers({
                            shortName: application.shortName,
                            startDate,
                            endDate,
                        }); //Get unique active users
                break;
            case UsersCategoryType.ActiveUsers:
                result =
                    chartType == 'line'
                        ? await this.getActiveUsersByDay({
                            startDate,
                            endDate,
                            application,
                        }) //Get unique active users by day
                        : await this.getActiveUsers({
                            startDate,
                            endDate,
                            application,
                        }); //Get unique active users
                break;
            case UsersCategoryType.EngagedUsers:
                result =
                    chartType == 'line'
                        ? await this.getEngagedUsersByDay({
                            startDate,
                            endDate,
                            application,
                        }) //Get unique engaged users by day
                        : await this.getEngagedUsers({
                            startDate,
                            endDate,
                            application,
                        }); //Get unique engaged users
                break;
            case UsersCategoryType.AverageInteractionUsers:
                result = await this.getActionsAverageInteractionByDay({
                    shortName: application.shortName,
                    startDate,
                    endDate,
                });
                break;
            case UsersCategoryType.TotalUsers:
                result = await this.getActionsTotalUsers({
                    shortName: application.shortName,
                    startDate,
                    endDate,
                });
                break;
        }
        return result.map((element) => ({
            category: category,
            action: category,
            ...element,
        }));
    }
    async getTotalActiveUsers({ startDate, endDate }) {
        const { count } = await this.MessagingHubService.sendCommand({
            method: 'get',
            to: 'postmaster@analytics.msging.net',
            uri: `/metrics/active-identity-quantity?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        }, 5 * 60 * 1000);
        return count;
    }
    async getTotalEngagedUsers({ startDate, endDate }) {
        const { count } = await this.MessagingHubService.sendCommand({
            method: 'get',
            to: 'postmaster@analytics.msging.net',
            uri: `/metrics/engaged-identity-quantity?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        }, 5 * 60 * 1000);
        return count;
    }
    async getActionUsersData(payload) {
        const { startDate, endDate, interval } = payload;
        const enableFT = await MetricsFeatures.isEnableMetricsToStatistics();
        const service = enableFT ? this.MetricsService : this.StatisticsService2;
        const { items } = await service.getUserStatistics(interval, startDate, endDate);
        return items;
    }
    async getActionsUniqueUsers(payload) {
        const data = await this.getActionUsersData({ interval: this.MetricsService.INTERVAL_NO_INERVAL, ...payload });
        return data.map((data) => ({
            count: data.count,
            action: UsersCategoryType.UniqueUsers,
        }));
    }
    async getActionsUniqueUsersByDay(payload) {
        const data = await this.getActionUsersData({ interval: this.MetricsService.INTERVAL_DAILY, ...payload });
        return data.map((day) => ({
            ...day,
            count: day.count,
            storageDate: moment(day.intervalStart).toJSON(),
        }));
    }
    async getActionsAverageInteractionByDay(payload) {
        //get user by day
        const usersByDay = await this.getActionUsersData({
            interval: this.MetricsService.INTERVAL_DAILY,
            ...payload,
        });
        //get received messages by day
        const { startDate, endDate } = payload;
        const enableFT = await MetricsFeatures.isEnableMetricsToStatistics();
        if (enableFT) {
            const { items: messagesByDay } = await this.MetricsService.getReceivedMessagesStatistics(this.MetricsService.INTERVAL_DAILY, startDate, endDate);
            return this.averageMessagePerUser(messagesByDay, usersByDay);
        }
        else {
            const { items: messagesByDay } = await this.StatisticsService2.getMessagesStatistics(this.StatisticsService2.INTERVAL_DAILY, this.StatisticsService2.AGGREGATION_TYPE_RECEIVED_BY, startDate, endDate);
            return this.averageMessagePerUser(messagesByDay, usersByDay);
        }
    }
    averageMessagePerUser(messagesByDay, usersByDay) {
        const averageMessagesPerUser = messagesByDay.map((m, i) => {
            if (m.count > 0) {
                return usersByDay[i].count == 0
                    ? m
                    : {
                        ...m,
                        count: Math.round(m.count / usersByDay[i].count),
                    };
            }
            else {
                return m;
            }
        });
        return averageMessagesPerUser.map((am) => ({
            count: am.count,
            storageDate: moment(am.intervalStart).toJSON(),
        }));
    }
    async getActiveUsers(payload) {
        const { startDate, endDate } = payload;
        const { items } = await this.MessagingHubService.sendCommand({
            method: 'get',
            to: 'postmaster@analytics.msging.net',
            uri: `/metrics/active-identity/NI?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        });
        return [
            {
                count: items[0].count,
                action: UsersCategoryType.ActiveUsers,
            },
        ];
    }
    async getActiveUsersByDay(payload) {
        const { startDate, endDate } = payload;
        const { items } = await this.MessagingHubService.sendCommand({
            method: 'get',
            to: 'postmaster@analytics.msging.net',
            uri: `/metrics/active-identity/D?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        }, GET_ACTIVE_USERS_BY_DAY_TIMEOUT);
        return items.map((day) => ({
            ...day,
            count: day.count,
            storageDate: moment(day.intervalStart).toJSON(),
        }));
    }
    async getEngagedUsers(payload) {
        const { startDate, endDate } = payload;
        const { items } = await this.MessagingHubService.sendCommand({
            method: 'get',
            to: 'postmaster@analytics.msging.net',
            uri: `/metrics/engaged-identity/NI?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        }, GET_ACTIVE_USERS_BY_DAY_TIMEOUT);
        return [
            {
                count: items[0].count,
                action: UsersCategoryType.EngagedUsers,
            },
        ];
    }
    async getEngagedUsersByDay(payload) {
        const { startDate, endDate } = payload;
        const { items } = await this.MessagingHubService.sendCommand({
            method: 'get',
            to: 'postmaster@analytics.msging.net',
            uri: `/metrics/engaged-identity/D?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        });
        return items.map((day) => ({
            ...day,
            count: day.count,
            storageDate: moment(day.intervalStart).toJSON(),
        }));
    }
    async getActionsTotalUsers(payload) {
        const [active, engaged] = await Promise.all([
            this.getActiveUsersByDay(payload),
            this.getEngagedUsersByDay(payload),
        ]);
        const activeUserEvents = active.map((day) => ({
            action: UsersCategoryType.ActiveUsers,
            count: day.count,
            storageDate: day.storageDate,
        }));
        const engagedUserEvents = engaged.map((day) => ({
            action: UsersCategoryType.EngagedUsers,
            count: day.count,
            storageDate: day.storageDate,
        }));
        return activeUserEvents.concat(engagedUserEvents);
    }
}
UserAnalyticsService.$inject = ['StatisticsService2', 'MetricsService', '$translate', 'MessagingHubService'];
